import React from 'react';
import { analytics } from 'lib/analytics';
import { styled } from '@mui/material/styles';
import { WarningImage, PageTitle, PageSubtitle } from 'features/freeTrial';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from 'components/Button';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';

export const ErrorType = Object.freeze({
  USER_ALREADY_EXISTS: 'user-already-exists',
  SITE_ALREADY_CREATED: 'site-already-created',
  GENERAL_ERROR: 'general',
  ANGLE_BRACKETS: 'no-angle-brackets-in-password',
  SWIPECLOCK_EMAIL: 'swipeclock-email',
  EMAIL_ALREADY_EXISTS: 'email-already-exists',
});

export default function ErrorsPage() {
  const { errorType: errorTypeFromParams } = useParams();
  const errorType = errorTypeFromParams || ErrorType.GENERAL_ERROR;

  analytics.freeTrial.trackError(errorType);

  switch (errorType) {
    case ErrorType.USER_ALREADY_EXISTS:
      return (
        <Content>
          <UserAlreadyExistsPage />
        </Content>
      );

    case ErrorType.SITE_ALREADY_CREATED:
      return (
        <Content>
          <SiteAlreadyCreatedPage />
        </Content>
      );

    case ErrorType.ANGLE_BRACKETS:
      return (
        <Content>
          <AngleBracketErrorPage />
        </Content>
      );

    case ErrorType.SWIPECLOCK_EMAIL:
      return (
        <Content sx={{ width: 'auto' }}>
          <SwipeclockEmailPage />
        </Content>
      );

    case ErrorType.EMAIL_ALREADY_EXISTS:
      return (
        <Content sx={{ width: 'auto' }}>
          <EmailAlreadyExistsPage />
        </Content>
      );

    default:
      return (
        <Content>
          <GeneralErrorPage />
        </Content>
      );
  }
}

function GeneralErrorPage() {
  return (
    <>
      <Stack direction="row" alignItems="center" gap={2}>
        <WarningImage />
        <PageTitle>Something went wrong!</PageTitle>
      </Stack>
      <Divider sx={{ mt: 2, mb: 4 }} />
      <Stack gap={2}>
        <PageSubtitle>Something went wrong, please try again.</PageSubtitle>
        <PageSubtitle>You may need to contact support.</PageSubtitle>
      </Stack>
    </>
  );
}

function AngleBracketErrorPage() {
  return (
    <>
      <Stack direction="row" alignItems="center" gap={2}>
        <WarningImage />
        <PageTitle>Something went wrong!</PageTitle>
      </Stack>
      <Divider sx={{ mt: 2, mb: 4 }} />
      <Stack gap={2}>
        <PageSubtitle>Something went wrong, please try again.</PageSubtitle>
        <PageSubtitle>Password cannot contain &lt; or &gt;.</PageSubtitle>
      </Stack>
    </>
  );
}

function SiteAlreadyCreatedPage() {
  return (
    <>
      <Stack direction="row" alignItems="center" gap={2}>
        <WarningImage />
        <PageTitle>Site Already Created</PageTitle>
      </Stack>
      <Divider sx={{ mt: 2, mb: 4 }} />
      <Stack gap={2}>
        <PageSubtitle>
          This site has already been created. You can log in through the
          <Button
            component={Link}
            href="https://app.workforcehub.com/login"
            target="_blank"
            rel="noopener noreferrer"
            variant="text"
          >
            login page.
          </Button>
        </PageSubtitle>
      </Stack>
    </>
  );
}

function UserAlreadyExistsPage() {
  return (
    <>
      <Stack direction="row" alignItems="center" gap={2}>
        <WarningImage />
        <PageTitle>User Already Exists</PageTitle>
      </Stack>
      <Divider sx={{ mt: 2, mb: 4 }} />
      <Stack gap={2}>
        <PageSubtitle>
          The email/password combination you've provided is already in use on
          another site!
        </PageSubtitle>
        <PageSubtitle>
          <Button variant="text" sx={{ left: '-8px' }}>
            Select a different password
          </Button>
          or
          <Button
            component={Link}
            href="https://app.workforcehub.com/login"
            target="_blank"
            rel="noopener noreferrer"
            variant="text"
            sx={{ ml: 1 }}
          >
            login into the existing site.
          </Button>
        </PageSubtitle>
      </Stack>
    </>
  );
}

function SwipeclockEmailPage() {
  return (
    <>
      <Stack direction="row" alignItems="center" gap={2}>
        <WarningImage />
        <PageTitle>"Swipeclock Email" Error</PageTitle>
      </Stack>
      <Divider sx={{ mt: 2, mb: 4 }} />
      <Stack gap={2}>
        <PageSubtitle sx={{ textWrap: 'wrap' }}>
          Sorry, you cannot continue with the free trial setup because you
          entered a Swipeclock email address. Swipeclock employees cannot set up
          sites using this process.
        </PageSubtitle>
        <PageSubtitle sx={{ textAlign: 'center' }}>
          <Button
            component={Link}
            href="https://www.workforcehub.com/start-free-trial"
          >
            OK
          </Button>
        </PageSubtitle>
      </Stack>
    </>
  );
}

function EmailAlreadyExistsPage() {
  return (
    <>
      <Stack direction="row" alignItems="center" gap={2}>
        <WarningImage />
        <PageTitle>Email Already Exists</PageTitle>
      </Stack>
      <Divider sx={{ mt: 2, mb: 4 }} />
      <Stack gap={2}>
        <PageSubtitle sx={{ textWrap: 'wrap' }}>
          Looks like we found your email in an existing WorkforceHub company.
          &nbsp;You can log into it through the&nbsp;&nbsp;
          <Button
            component={Link}
            href="https://app.workforcehub.com/login"
            variant="text"
            sx={{ ml: -1 }}
          >
            LOGIN PAGE.
          </Button>
          If you have problems accessing your account or you need to set up a
          new site, please visit our&nbsp;&nbsp;
          <Button
            component={Link}
            href="https://workforcehub.com/workforcehub-university/#live-chat-support"
            variant="text"
            sx={{ ml: -1 }}
          >
            WorkforceHub page
          </Button>
          and use the support chat.
        </PageSubtitle>
      </Stack>
    </>
  );
}

const Content = styled(Box)(({ theme }) => ({
  width: 'max-content',
  margin: `${theme.spacing(4)} auto`,
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(0),
  },
  [theme.breakpoints.down('md')]: {
    width: 'auto',
  },
}));
